import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService, apiClient } from '../../../services/api';
import '../node/QuickAddNodeModal.css'; // shared quick-add styles

/**
 * QuickAddSynapseModal - Simplified synapse creation with auto-populated Platform fields.
 *
 * Only requires: sourceNode, targetNode, synapse kind.
 * Optional: custom relation name, description.
 * All Platform parameters (SynapseEffectiveSettings, Weight, DelayMs, Pinecone)
 * are auto-populated by the backend.
 */

function NodeSearchField({ label, graphId, selected, onSelect, onClear }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2 || !graphId) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await apiService.listNodes({ graphId, query: q, pageSize: 20 });
      if (resp.success) {
        setResults(resp.nodes || []);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  if (selected) {
    return (
      <div className="qa-modal-field">
        <label>{label} <span className="qa-required">*</span></label>
        <div className="qa-selected-node">
          <span>{selected.name} <small>({selected.type || 'Node'})</small></span>
          <button onClick={onClear}>&times;</button>
        </div>
      </div>
    );
  }

  return (
    <div className="qa-modal-field">
      <label>{label} <span className="qa-required">*</span></label>
      <div className="qa-node-search">
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search nodes by name..."
        />
        {showDropdown && (query.length >= 2) && (
          <div className="qa-node-dropdown">
            {loading && <div className="qa-node-option" style={{ color: '#6b7280' }}>Searching...</div>}
            {!loading && results.length === 0 && <div className="qa-node-option" style={{ color: '#6b7280' }}>No nodes found</div>}
            {results.map((node) => {
              const nName = node.name || node.nodeName;
              const nodeAliases = node.aliases || [];
              const q = query.toLowerCase();
              const matchingAlias = nodeAliases.find(a =>
                a.toLowerCase().includes(q) && a.toLowerCase() !== (nName || '').toLowerCase()
              );
              return (
                <div
                  key={node.id || node.nodeId}
                  className="qa-node-option"
                  onClick={() => {
                    onSelect({
                      id: node.id || node.nodeId,
                      name: nName,
                      type: node.type || node.nodeType || '',
                    });
                    setShowDropdown(false);
                    setQuery('');
                  }}
                >
                  <span className="qa-node-option-name">
                    {nName}
                    {matchingAlias && (
                      <span style={{ color: '#888', fontSize: '0.85em', marginLeft: 4 }}>
                        (aka: {matchingAlias})
                      </span>
                    )}
                  </span>
                  <span className="qa-node-option-type">{node.type || node.nodeType || ''}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickAddSynapseModal({ isOpen, onClose, onSynapseCreated, graphId }) {
  const [sourceNode, setSourceNode] = useState(null);
  const [targetNode, setTargetNode] = useState(null);
  const [synapseKindId, setSynapseKindId] = useState('');
  const [customRelation, setCustomRelation] = useState('');
  const [description, setDescription] = useState('');

  const [synapseKinds, setSynapseKinds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch synapse kinds from API
  useEffect(() => {
    const fetchKinds = async () => {
      try {
        const resp = await apiClient.get('/edges/options/edge-kinds');
        if (resp.data.success && resp.data.kinds) {
          setSynapseKinds(resp.data.kinds);
          const excKind = resp.data.kinds.find(k => k.kindName === 'exc');
          if (excKind) {
            setSynapseKindId(excKind.id);
          } else if (resp.data.kinds.length > 0) {
            setSynapseKindId(resp.data.kinds[0].id);
          }
        }
      } catch {
        // best-effort
      }
    };
    fetchKinds();
  }, []);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSourceNode(null);
      setTargetNode(null);
      const excKind = synapseKinds.find(k => k.kindName === 'exc');
      setSynapseKindId(excKind ? excKind.id : (synapseKinds[0]?.id || ''));
      setCustomRelation('');
      setDescription('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen, synapseKinds]);

  const selectedKind = synapseKinds.find(k => k.id === synapseKindId);
  const effectiveRelation = customRelation.trim() || (selectedKind?.kindName || 'related_to');

  const handleSubmit = useCallback(async () => {
    if (!sourceNode) { setError('Source node is required'); return; }
    if (!targetNode) { setError('Target node is required'); return; }
    if (!synapseKindId) { setError('Connection kind is required'); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiService.quickAddSynapse({
        graphId,
        sourceNodeId: sourceNode.id,
        targetNodeId: targetNode.id,
        relation: effectiveRelation,
        synapseKindId,
        description: description.trim() || undefined,
      });

      if (result.success) {
        setSuccess('Connection created successfully');
        if (onSynapseCreated) onSynapseCreated(result);
        setTimeout(() => onClose(), 600);
      } else {
        setError(result.message || 'Failed to create synapse');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to create synapse');
    } finally {
      setSaving(false);
    }
  }, [graphId, sourceNode, targetNode, synapseKindId, effectiveRelation, description, onSynapseCreated, onClose]);

  if (!isOpen) return null;

  return (
    <div className="qa-modal-overlay" onClick={onClose}>
      <div className="qa-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="qa-modal-header">
          <div>
            <p className="qa-modal-eyebrow">Quick Add</p>
            <h2>New Connection</h2>
          </div>
          <button className="qa-modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="qa-modal-body">
          {error && <div className="qa-modal-alert qa-modal-alert--error">{error}</div>}
          {success && <div className="qa-modal-alert qa-modal-alert--success">{success}</div>}

          <NodeSearchField
            label="Source Node"
            graphId={graphId}
            selected={sourceNode}
            onSelect={setSourceNode}
            onClear={() => setSourceNode(null)}
          />

          <NodeSearchField
            label="Target Node"
            graphId={graphId}
            selected={targetNode}
            onSelect={setTargetNode}
            onClear={() => setTargetNode(null)}
          />

          <div className="qa-modal-field">
            <label>Connection Kind <span className="qa-required">*</span></label>
            <select value={synapseKindId} onChange={(e) => setSynapseKindId(e.target.value)}>
              {synapseKinds.length === 0 && (
                <option value="">Loading kinds...</option>
              )}
              {synapseKinds.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kindName}{k.hasNeuralProfile === false ? ' (no profile)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="qa-modal-field">
            <label>Relation Name (optional)</label>
            <input
              type="text"
              value={customRelation}
              onChange={(e) => setCustomRelation(e.target.value)}
              placeholder={`Defaults to "${selectedKind?.kindName || 'related_to'}"`}
            />
          </div>

          <div className="qa-modal-field">
            <label>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this relationship..."
              rows={2}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="qa-modal-footer">
          <button className="qa-btn qa-btn--secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="qa-btn qa-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating...' : 'Create Connection'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickAddSynapseModal;
